export function transformTemplate(apiTemplate: any) {
  if (!apiTemplate) return null;

  if (apiTemplate.header || apiTemplate.body || apiTemplate.footer) {
    return apiTemplate;
  }

  const components = apiTemplate.metaPayload?.components || [];
  const isAuth = apiTemplate.category?.toLowerCase() === 'authentication';

  // Handle both lowercase and uppercase component types
  const header = components.find((c: any) =>
    c.type?.toLowerCase() === 'header'
  );
  const body = components.find((c: any) =>
    c.type?.toLowerCase() === 'body'
  );
  const footer = components.find((c: any) =>
    c.type?.toLowerCase() === 'footer'
  );
  const buttonsComponent = components.find((c: any) =>
    c.type?.toLowerCase() === 'buttons'
  );

  // Extract buttons from BUTTONS component
  const buttons = (buttonsComponent?.buttons || []).map((b: any) => {
    if (b.type?.toLowerCase() === 'otp' && !b.text) {
      return { ...b, text: 'Copy code' };
    }
    return b;
  });

  const bodyText = body?.text || (isAuth ? '{{1}} is your verification code.' : '');

  // Extract variables from body text
  const variables: Array<{ index: number; name?: string; example?: string; fromHeader?: boolean }> = [];
  if (bodyText) {
    const matches = bodyText.match(/\{\{([^}]+)\}\}/g);
    if (matches) {
      matches.forEach((match: string, idx: number) => {
        const content = match.replace(/\{\{|\}\}/g, '').trim();
        const isPositional = /^\d+$/.test(content);

        // Check for examples in body
        let example: string | undefined;
        if (body?.example?.body_text) {
          const examples = body.example.body_text;
          if (Array.isArray(examples) && examples[idx]) {
            example = examples[idx][0] || examples[idx];
          }
        }
        if (body?.example?.body_text_named_params) {
          const namedParam = body.example.body_text_named_params.find(
            (p: any) => p.param_name === content
          );
          if (namedParam?.example) {
            example = namedParam.example;
          }
        }

        variables.push({
          index: isPositional ? parseInt(content) : idx + 1,
          name: isPositional ? undefined : content,
          example,
          fromHeader: false,
        });
      });
    }
  }

  // Also extract variables from header text
  if (header?.text) {
    const headerMatches = header.text.match(/\{\{([^}]+)\}\}/g);
    if (headerMatches) {
      headerMatches.forEach((match: string, idx: number) => {
        const content = match.replace(/\{\{|\}\}/g, '').trim();
        const isPositional = /^\d+$/.test(content);

        let example: string | undefined;
        if (header.example?.header_text) {
          const examples = header.example.header_text;
          if (Array.isArray(examples) && examples[idx]) {
            example = examples[idx][0] || examples[idx];
          }
        }

        variables.push({
          index: isPositional ? parseInt(content) : idx + 1,
          name: isPositional ? undefined : content,
          example,
          fromHeader: true,
        });
      });
    }
  }

  return {
    ...apiTemplate,
    header: header ? {
      type: (header.format || header.type || 'text')?.toUpperCase(),
      text: header.text,
      mediaExample: header.example?.header_handle?.[0] || "",
      mediaPreview: apiTemplate.mediaUrl || header.mediaUrl || "",
      filename: header.filename
    } : null,
    body: bodyText,
    footer: footer?.text || '',
    buttons: buttons,
    variables: variables
  };
}

function hasNamedVariables(text: string = ""): boolean {
  return /\{\{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*\}\}/.test(text);
}

function hasPositionalVariables(text: string = ""): boolean {
  return /\{\{\s*\d+\s*\}\}/.test(text);
}

export function inferVariableTypeFromTemplate(template: any): 'named' | 'positional' {
  if (!template) return 'positional';

  const rawFormat = String(
    template.metaPayload?.parameter_format ||
    template.parameter_format ||
    template.parameterFormat ||
    ''
  ).trim().toLowerCase();

  const components = template.metaPayload?.components || [];
  const headerText = components.find((c: any) => c.type?.toLowerCase() === 'header')?.text || template.header?.text || '';
  const bodyText = components.find((c: any) => c.type?.toLowerCase() === 'body')?.text || template.body || '';

  const named = hasNamedVariables(headerText) || hasNamedVariables(bodyText);
  const positional = hasPositionalVariables(headerText) || hasPositionalVariables(bodyText);

  if (named && !positional) return 'named';
  if (positional && !named) return 'positional';
  if (rawFormat === 'named') return 'named';
  if (rawFormat === 'positional') return 'positional';
  if (named) return 'named';
  return 'positional';
}
